'use client'

import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { ReactNode, useMemo } from 'react'
import { B2bBannerSearch } from '@/features/b2b/b2b-banner-search'
import { B2bContextProvider } from '@/features/b2b/b2b-context'
import { B2bHero } from '@/features/b2b/b2b-hero'
import { B2bMapSearch } from '@/features/b2b/b2b-map-search'
import { CommonBenefits } from '@/features/common/common-benefits'
import { CommonLogosBanner } from '@/features/common/common-logos-banner'
import { CommonSection } from '@/features/common/common-section'
import { MarketingVideo } from '@/features/marketing-videos/marketing-video'
import { NeotasteExplanationVideo } from '@/features/marketing-videos/neotaste-explanation-video'
import { Locale } from '@/setup/locales'
import { cn } from '@/utils/styles/cn'
import { CommonSlider } from '../common/common-slider'

export const B2bLandingPage = ({ className }: { className?: string }) => {
    const t = useTranslations()
    const locale = useLocale()

    const b2bPartnerLogoBannerItems = useMemo<ReactNode[]>(() => {
        return [
            <Image
                height={120}
                width={200}
                alt="ABOUT YOU"
                priority
                src="/_images/b2b/about-you.svg"
                key="ABOUT YOU"
            />,

            <Image
                priority
                height={70}
                width={127}
                alt="Maloa"
                src="/_images/b2b/omr.png"
                key="OMR"
            />,

            <Image
                height={48}
                width={166}
                alt="Oceans Apart"
                priority
                src="/_images/b2b/oceans-apart.webp"
                key="Oceans Apart"
            />,

            <Image
                height={66}
                width={108}
                alt="Pets Deli"
                priority
                src="/_images/b2b/pets-deli.png"
                key="Pets Deli"
            />,
        ]
    }, [])

    const restaurantPartnerLogoItems = useMemo<ReactNode[]>(() => {
        return [
            <Image
                height={96}
                width={140}
                alt="Hans im Glück"
                src="/_images/become-a-partner/hans-im-glueck.png"
                key="hans-im-glueck"
                className="min-w-fit"
            />,

            <Image
                height={50}
                width={127}
                alt="Maloa"
                src="/_images/become-a-partner/maloa.png"
                key="maloa"
                className="min-w-fit"
            />,

            <Image
                height={48}
                width={166}
                alt="L'Osteria"
                src="/_images/become-a-partner/losteria.png"
                key="L'Osteria"
                className="min-w-fit"
            />,

            <Image
                height={66}
                width={108}
                alt="Volt Restaurant"
                src="/_images/become-a-partner/volt.png"
                key="Volt Restaurant"
                className="min-w-fit"
            />,
        ]
    }, [])

    const writtenTestimonials = useMemo(() => {
        return [
            {
                image: '/_images/b2b/cornelia-siwak.jpeg',
                quote: t('for-companies.testimonials.deepblue.quote'),
                name: 'Cornelia Siwak',
                position: t('for-companies.testimonials.deepblue.position'),
            },
            {
                image: '/_images/b2b/lisa-schleiwies.jpeg',
                quote: t('for-companies.testimonials.about-you.quote'),
                name: 'Lisa Schleiwies',
                position: t('for-companies.testimonials.about-you.position'),
            },
        ]
    }, [t])

    return (
        <B2bContextProvider>
            <div className={cn('flex w-full flex-col items-center', className)}>
                <div className="mt-4 md:mt-12" />

                <B2bHero />

                <CommonLogosBanner items={b2bPartnerLogoBannerItems} />

                {locale === Locale.DE_DE && (
                    <CommonSection title={t('for-companies.videos.title')} className="pt-10">
                        <MarketingVideo
                            src="https://web.neotastecdn.com/videos/NeoTaste-AboutYou.mp4"
                            aspectRatio={16 / 9}
                            title="NeoTaste x ABOUT YOU"
                            className={cn(
                                `max-w-[90vw] min-w-[90vw] max-h-[50vw] min-h-[50vw] md:max-w-[70vw] md:min-w-[70vw] md:max-h-[40vw]
                                md:min-h-[40vw]`,
                                className
                            )}
                        />
                    </CommonSection>
                )}

                <CommonBenefits tBaseKey="for-companies.employeeBenefits" />

                <B2bMapSearch />

                <CommonLogosBanner items={restaurantPartnerLogoItems} />

                <div className="py-20 w-full flex flex-col items-center justify-center">
                    <CommonSlider type="testimonials" testimonials={writtenTestimonials} />
                </div>

                <CommonBenefits
                    tBaseKey="for-companies.companyBenefits"
                    itemsContainerClassName="grid-cols-1 px-8"
                />

                <CommonSection title={t('for-companies.explanation-video.title')} className="pt-10">
                    <NeotasteExplanationVideo />
                </CommonSection>

                <B2bBannerSearch />
            </div>
        </B2bContextProvider>
    )
}
